import { CopyIdButton, DetailPanel, SingleValuePanel, type PanelSpan } from '@/components/shared';
import {
  formatDateTime,
  formatLinkStateLabel,
  formatResolutionStateLabel,
  formatTargetTypeLabel,
} from '../format';
import type { BackofficeLink, BackofficeLinkDestination } from '../types';

function DetailRow({
  label,
  value,
  copyValue,
  copyLabel,
}: {
  label: string;
  value: string;
  copyValue?: string;
  copyLabel?: string;
}) {
  return (
    <div className="grid gap-1 border-b border-neutral-100 py-3 last:border-b-0 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-4">
      <dt className="font-semibold text-neutral-900">{label}</dt>
      <dd className="min-w-0 text-neutral-700">
        {copyValue && copyLabel ? (
          <div className="group inline-flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate">{value}</span>
            <CopyIdButton value={copyValue} label={copyLabel} />
          </div>
        ) : (
          <span>{value}</span>
        )}
      </dd>
    </div>
  );
}

function resolveDestinationSummary(destination: BackofficeLinkDestination): string {
  if (destination.targetType === 'study') {
    return destination.project?.title ?? destination.projectId ?? 'Study';
  }

  return destination.url ?? 'No URL';
}

export function LinkDetailsTile({
  link,
  currentSpan,
  panelId,
}: {
  link: BackofficeLink;
  currentSpan?: PanelSpan;
  panelId?: string;
}) {
  const detailItems = [
    {
      id: 'slug',
      label: 'Slug',
      content: (
        <DetailRow
          key="slug"
          label="Slug"
          value={link.slug}
          copyValue={link.slug}
          copyLabel="Copy link slug"
        />
      ),
    },
    {
      id: 'short-url',
      label: 'Short URL',
      content: (
        <DetailRow
          key="short-url"
          label="Short URL"
          value={link.shortUrl}
          copyValue={link.shortUrl}
          copyLabel="Copy short URL"
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      content: <DetailRow key="status" label="Status" value={formatLinkStateLabel(link.status)} />,
    },
    {
      id: 'resolution',
      label: 'Resolution',
      content: (
        <DetailRow
          key="resolution"
          label="Resolution"
          value={formatResolutionStateLabel(link.currentResolution.state)}
        />
      ),
    },
    {
      id: 'created',
      label: 'Created',
      content: <DetailRow key="created" label="Created" value={formatDateTime(link.createdAt)} />,
    },
    {
      id: 'updated',
      label: 'Updated',
      content: <DetailRow key="updated" label="Updated" value={formatDateTime(link.updatedAt)} />,
    },
    {
      id: 'archived',
      label: 'Archived',
      content: <DetailRow key="archived" label="Archived" value={formatDateTime(link.deletedAt)} />,
    },
  ];

  return (
    <DetailPanel
      title="Link details"
      span="2x3"
      currentSpan={currentSpan}
      panelId={panelId}
      settingsItems={detailItems}
    >
      <dl className="text-body">{detailItems.map((item) => item.content)}</dl>
    </DetailPanel>
  );
}

export function LinkCurrentTargetTile({
  link,
  currentSpan,
  panelId,
}: {
  link: BackofficeLink;
  currentSpan?: PanelSpan;
  panelId?: string;
}) {
  const destination = link.currentDestination;
  const detailItems = destination
    ? [
        {
          id: 'target-type',
          label: 'Target type',
          content: (
            <DetailRow
              key="target-type"
              label="Target type"
              value={formatTargetTypeLabel(destination.targetType)}
            />
          ),
        },
        {
          id: 'target',
          label: 'Target',
          content: (
            <DetailRow key="target" label="Target" value={resolveDestinationSummary(destination)} />
          ),
        },
        {
          id: 'description',
          label: 'Description',
          content: (
            <DetailRow
              key="description"
              label="Description"
              value={destination.description ?? 'None'}
            />
          ),
        },
        {
          id: 'activated',
          label: 'Activated',
          content: (
            <DetailRow
              key="activated"
              label="Activated"
              value={formatDateTime(destination.activatedAt)}
            />
          ),
        },
        {
          id: 'updated-by',
          label: 'Updated by',
          content: (
            <DetailRow key="updated-by" label="Updated by" value={destination.activatedByEmail} />
          ),
        },
        {
          id: 'resolved-url',
          label: 'Resolved URL',
          content: (
            <DetailRow
              key="resolved-url"
              label="Resolved URL"
              value={link.currentResolution.url ?? 'Awaiting publish'}
              {...(link.currentResolution.url ? { copyValue: link.currentResolution.url } : {})}
              copyLabel="Copy resolved URL"
            />
          ),
        },
      ]
    : undefined;

  return (
    <DetailPanel
      title="Current target"
      span="2x3"
      currentSpan={currentSpan}
      panelId={panelId}
      settingsItems={detailItems}
    >
      {destination ? (
        <dl className="text-body">{detailItems?.map((item) => item.content)}</dl>
      ) : (
        <div className="py-8 text-center text-body text-neutral-500">No active target.</div>
      )}
    </DetailPanel>
  );
}

export function LinkDestinationHistoryTile({
  link,
  currentSpan,
  panelId,
}: {
  link: BackofficeLink;
  currentSpan?: PanelSpan;
  panelId?: string;
}) {
  const destinations = [...(link.destinations ?? [])].sort((left, right) =>
    right.activatedAt.localeCompare(left.activatedAt)
  );

  return (
    <DetailPanel
      title="Destination history"
      span="4x3"
      currentSpan={currentSpan}
      panelId={panelId}
      contentOverflow="auto"
    >
      {destinations.length === 0 ? (
        <div className="py-8 text-center text-body text-neutral-500">No destinations yet.</div>
      ) : (
        <div className="divide-y divide-neutral-100">
          {destinations.map((destination) => (
            <div key={destination.id} className="space-y-2 py-4 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-neutral-900">
                  {formatTargetTypeLabel(destination.targetType)}
                </span>
                {destination.isCurrent ? (
                  <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-small font-medium text-emerald-700">
                    Current
                  </span>
                ) : null}
              </div>
              <div className="break-all text-body text-neutral-700">
                {resolveDestinationSummary(destination)}
              </div>
              <div className="text-small text-neutral-500">
                Activated {formatDateTime(destination.activatedAt)} by{' '}
                {destination.activatedByEmail}
              </div>
              <div className="text-small text-neutral-500">
                Deactivated {formatDateTime(destination.deactivatedAt)}
              </div>
              {destination.description ? (
                <div className="text-small text-neutral-600">{destination.description}</div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </DetailPanel>
  );
}

export function LinkTrackingUsageTile({
  usageCount,
  currentSpan,
  panelId,
}: {
  usageCount: number;
  currentSpan?: PanelSpan;
  panelId?: string;
}) {
  return (
    <SingleValuePanel
      title="Usage"
      value={usageCount.toLocaleString('en-US')}
      footer="Tracked visits"
      valueTone="neutral"
      panelId={panelId}
      currentSpan={currentSpan}
    />
  );
}
